import { useFaultStudyCombined } from '../../hooks/useFaultAnalysis.ts'
import { AsOfStamp } from './AsOfStamp.tsx'
import { DUTY_STATUS_COLORS } from '../../types/faultAnalysis.ts'
import { collidingNames, disambiguatedName } from '../../utils/deviceNames.ts'
import { faultCurrentTypeLabel } from '../../utils/faultLabels.ts'
import {
  IndicativeBanner,
  INDICATIVE_VALUE_COLOR,
  NEUTRAL_BADGE_CLASS,
  SetupDataChip,
} from './indicative.tsx'
import { Term } from '../common/Term.tsx'
import { AssumptionsDisclosure } from './AssumptionsDisclosure.tsx'
import { friendlyError } from '../../utils/friendlyError.ts'

interface DeviceDutyProps {
  feederId: number
}

const STATUS_BADGES: Record<string, string> = {
  adequate: 'bg-green-100 text-green-700',
  marginal: 'bg-yellow-100 text-yellow-700',
  inadequate: 'bg-red-100 text-red-700',
  unknown: 'bg-gray-100 text-gray-600',
}

export function DeviceDuty({ feederId }: DeviceDutyProps) {
  const { data, isLoading, error } = useFaultStudyCombined(feederId, r => r.duty)

  if (isLoading) {
    return <div className="text-sm text-gray-400 p-4">Evaluating device duty...</div>
  }

  if (error) {
    return (
      <div className="text-sm text-red-600 p-4">
        {friendlyError(error, 'load device duty')}
      </div>
    )
  }

  if (!data) {
    return <div className="text-sm text-gray-400 p-4">No device duty data available</div>
  }

  // C8: names borne by 2+ devices in this result set get " · #<id>" appended.
  const colliding = collidingNames(
    data.devices.map(d => ({ name: d.device_name, id: d.device_id })),
  )

  // A1 demotion: grey counts/values and neutralise status badges while
  // indicative.
  const indicative = data.indicative_only
  const muted = indicative ? ' indicative-muted' : ''
  const countColor = (c: string) => (indicative ? INDICATIVE_VALUE_COLOR : c)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Device Duty Evaluation</h3>
        <span className="text-xs text-gray-500">{data.total_devices} devices</span>
      </div>
      <AsOfStamp computedAt={data.analysis_timestamp} />

      {indicative && <IndicativeBanner />}

      {/* Summary badges */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold" style={{ color: countColor(DUTY_STATUS_COLORS.adequate) }}>
            {data.adequate_count}
          </div>
          <div className="text-xs text-gray-500 mt-1">Adequate</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold" style={{ color: countColor(DUTY_STATUS_COLORS.marginal) }}>
            {data.marginal_count}
          </div>
          <div className="text-xs text-gray-500 mt-1">Marginal</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold" style={{ color: countColor(DUTY_STATUS_COLORS.inadequate) }}>
            {data.inadequate_count}
          </div>
          <div className="text-xs text-gray-500 mt-1">Inadequate</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold" style={{ color: countColor(DUTY_STATUS_COLORS.unknown) }}>
            {data.unknown_count}
          </div>
          <div className="text-xs text-gray-500 mt-1">Unknown</div>
        </div>
      </div>

      {/* Warnings render once per tab via WarningSummary (spec §3.1). */}

      {/* A3 (spec §3.5): the modelling assumptions this run actually used. */}
      <AssumptionsDisclosure assumptions={data.assumptions ?? []} />

      {/* Comparison basis explanation */}
      <div className="text-xs text-gray-400 italic">
        Duty ratio = available fault current / device interrupting rating.
        Marginal: 80-100%. Inadequate: device rating exceeded.
        Fuses compared against max(3φ, SLG); reclosers against asymmetrical RMS.
      </div>

      {/* Device table */}
      {data.devices.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
            Devices ({data.devices.length})
            {indicative && <SetupDataChip />}
          </h4>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-200 text-left text-gray-500">
                  <th className="pb-1 pr-2">Device</th>
                  <th className="pb-1 pr-2">Type</th>
                  <th className="pb-1 pr-2">GIS ID</th>
                  <th className="pb-1 pr-2 text-right">Fault (kA)</th>
                  <th className="pb-1 pr-2">Basis</th>
                  <th className="pb-1 pr-2 text-right"><Term k="interrupting">Rating (kA)</Term></th>
                  <th className="pb-1 pr-2 text-right">Duty %</th>
                  <th className="pb-1">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.devices.map(d => (
                  <tr key={d.device_id} className="border-b border-gray-50">
                    <td className="py-1 pr-2 text-gray-700 font-medium">
                      {disambiguatedName(d.device_name, d.device_id, colliding) || `Device #${d.device_id}`}
                    </td>
                    <td className="py-1 pr-2 text-gray-600 capitalize">{d.device_type.replace(/_/g, ' ')}</td>
                    <td className="py-1 pr-2 text-gray-500 font-mono text-xs">{d.gis_id || '\u2014'}</td>
                    <td className={`py-1 pr-2 text-right text-gray-800 font-medium${muted}`}>
                      {d.fault_current_ka.toFixed(2)}
                    </td>
                    <td className="py-1 pr-2 text-gray-500">{faultCurrentTypeLabel(d.fault_current_type)}</td>
                    <td className={`py-1 pr-2 text-right text-gray-600${muted}`}>
                      {d.interrupting_rating_ka !== null ? d.interrupting_rating_ka.toFixed(1) : '\u2014'}
                    </td>
                    <td
                      className={`py-1 pr-2 text-right font-medium${muted}`}
                      style={indicative ? undefined : { color: DUTY_STATUS_COLORS[d.status] }}
                    >
                      {d.duty_ratio_pct !== null ? `${d.duty_ratio_pct.toFixed(1)}%` : '\u2014'}
                    </td>
                    <td className="py-1">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${indicative ? NEUTRAL_BADGE_CLASS : STATUS_BADGES[d.status] || STATUS_BADGES.unknown}`}>
                        {d.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.devices.length === 0 && (
        <div className="text-sm text-gray-400 p-2">No protective devices found on this feeder</div>
      )}
    </div>
  )
}
